import { EventEmitter, namespace } from '../../../../../FrontendBundle/Resources/js/ofcs';

/**
 * Command history
 *
 * @class History
 * @extends EventEmitter
 */
export default class History extends EventEmitter {
    /**
     * @cfg {Array} commands
     */

    /**
     * Triggered when the internal state changed.
     * Includes pointer move, command add
     *
     * @event statechange
     * @param {History} history Me
     */

    /**
     * Triggered when a command is undone
     *
     * @event undo
     * @param {History} history Me
     * @param {AbstractCommand} command Command that is undone
     */

    /**
     * Triggered when a command is redone
     *
     * @event redo
     * @param {History} history Me
     * @param {AbstractCommand} command Command that is redone
     */

    /**
     * Creates a new instance of the history
     *
     * @constructor
     * @param {CommandExecutor} commandExecutor commandExecutor
     */
    constructor(commandExecutor) {
        super();
        this.clear();
        this.commandExecutor = commandExecutor;
    }

    /**
     * Add command to history.
     * Raises pointer by one.
     *
     * @param {AbstractCommand} command Command
     * @return {boolean}
     */
    addCommand(command) {
        if (!command.isValid()) return false;
        if (this.canRedo()) this.commands.splice(this.pointer + 1);

        this.commands.push(command);
        this.length = this.commands.length;

        this.pointer += 1;

        this.trigger('statechange', this);

        return true;
    }

    /**
     * Add command to history and execute it.
     *
     * @param {AbstractCommand} command Command
     * @return {boolean}
     */
    addAndExecuteCommand(command) {
        if (!this.commandExecutor.execute(command)) return false;

        return this.addCommand(command);
    }

    /**
     * Undo last command in history.
     * Moves pointer down one step.
     *
     * @return {AbstractCommand}
     */
    undo() {
        if (!this.canUndo()) return null;
        if (!this.commands[this.pointer]) throw new Error('Command not found');

        const undoCommand = this.commands[this.pointer];

        this.commandExecutor.unexecute(undoCommand);
        this.pointer -= 1;

        this.trigger('undo', this, undoCommand);
        this.trigger('statechange', this);

        return undoCommand;
    }

    /**
     * Undoes command and also removes
     * it completely from history
     */
    undoAndRemove() {
        this.undo();
        this.commands.pop();
        this.length = this.commands.length;
        this.trigger('statechange', this);
    }

    /**
     * Redo next command in history
     * Moves pointer up one step.
     *
     * @return {AbstractCommand}
     */
    redo() {
        if (!this.canRedo()) return null;
        if (!this.commands[this.pointer + 1]) throw new Error('Command not found');

        this.pointer += 1;

        const redoCommand = this.commands[this.pointer];

        this.commandExecutor.execute(redoCommand);

        this.trigger('redo', this, redoCommand);
        this.trigger('statechange', this);

        return redoCommand;
    }

    /**
     * Undo to a given command
     *
     * @param {AbstractCommand} toCommand
     */
    undoTo(toCommand) {
        let i = this.pointer;
        let command;

        while (command !== toCommand && i >= 0) {
            command = this.undo();
            i -= 1;
        }
    }

    /**
     * Redo to a given command
     *
     * @param {AbstractCommand} toCommand
     */
    redoTo(toCommand) {
        let i = this.pointer;
        let command;

        while (command !== toCommand && i <= this.length) {
            command = this.redo();
            i += 1;
        }
    }

    /**
     * Can undo be called?
     *
     * @return {boolean}
     */
    canUndo() {
        return this.pointer > -1;
    }

    /**
     * Can redo be called?
     *
     * @return {boolean}
     */
    canRedo() {
        return this.pointer < (this.commands.length - 1);
    }

    /**
     * Clear history, resets pointer
     */
    clear() {
        this.pointer = -1;
        this.commands = [];
        this.length = 0;
    }

    /**
     * Return all commands
     *
     * @return {AbstractCommand[]}
     */
    getCommands() {
        return this.commands;
    }

    each(callback) {
        let i;

        for (i = 0; i < this.commands.length; i += 1) {
            callback(this.commands[i], i, i < (this.pointer + 1) ? 'undo' : 'redo');
        }
    }
}

namespace('Ofcs.Editor.Command');
window.Ofcs.Editor.Command.History = History;
